import { useEffect, useMemo, useState } from "react";
import { useLeague } from "./LeagueLayout";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Trophy, AlertTriangle, Zap, Coins, Filter, ChevronDown, ChevronUp, XCircle, CheckCircle2, PauseCircle } from "lucide-react";

/**
 * Storico Sponsorship di lega. Mostra:
 *  - Aggregati per team (premi vinti, bet raddoppiati, penalità, netto)
 *  - Lista dettagliata con stato + esito
 * Accessibile a tutti i membri della lega.
 */
export default function LeagueSponsorHistory() {
  const { league } = useLeague();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("closed"); // active | closed | all
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter === "closed") params.status = "achieved,failed,cancelled";
      else if (statusFilter === "active") params.status = "active";
      if (teamFilter !== "all") params.team_id = teamFilter;
      const r = await api.get(`/leagues/${league.league_id}/sponsorships/history`, { params });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [league.league_id, teamFilter, statusFilter]);

  const teamsOptions = useMemo(() => {
    const map = new Map();
    (data?.items || []).forEach(i => { if (!map.has(i.team_id)) map.set(i.team_id, i.team_name || "—"); });
    (data?.aggregates || []).forEach(a => { if (!map.has(a.team_id)) map.set(a.team_id, a.team_name); });
    return [...map.entries()].map(([id, name]) => ({ team_id: id, name }));
  }, [data]);

  const topWinner = data?.aggregates?.[0];
  const totalAch = (data?.items || []).filter(i => i.status === "achieved").length;
  const totalFail = (data?.items || []).filter(i => i.status === "failed").length;

  return (
    <div className="space-y-6" data-testid="sponsor-history-page">
      {/* Hero */}
      <div className="rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-slate-900 via-slate-950 to-emerald-950/20 p-5 sm:p-7 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none"/>
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <Trophy className="text-emerald-400" size={22}/>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white">Storico Sponsor</h1>
          </div>
          <div className="flex-1 text-sm text-slate-300">
            {topWinner
              ? <>Team più premiato: <span className="font-black text-emerald-300">{topWinner.team_name}</span> con <span className="font-black text-white">{topWinner.net_delta > 0 ? "+" : ""}{topWinner.net_delta}c</span> di netto. Sponsorship chiuse: <span className="font-black text-emerald-300">{totalAch}</span> vinte / <span className="font-black text-rose-300">{totalFail}</span> perse.</>
              : <>Nessuno sponsor finalizzato ancora. Torna qui a fine stagione per vedere premi e bet raddoppiati.</>}
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-400 font-black">
          <Filter size={11}/> Filtri:
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          data-testid="filter-status"
          className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-700 text-xs text-white">
          <option value="closed">Chiuse (achieved/failed)</option>
          <option value="active">Attive</option>
          <option value="all">Tutte</option>
        </select>
        <select value={teamFilter} onChange={e => setTeamFilter(e.target.value)}
          data-testid="filter-team"
          className="px-2 py-1 rounded-lg bg-slate-900 border border-slate-700 text-xs text-white">
          <option value="all">Tutti i team</option>
          {teamsOptions.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
        </select>
      </div>

      {loading && <div className="text-slate-400 text-sm">Caricamento…</div>}

      {/* Aggregati per team */}
      {!loading && data?.aggregates?.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4" data-testid="aggregates-panel">
          <h2 className="text-sm font-black uppercase tracking-widest text-white mb-3 flex items-center gap-2">
            <Coins size={14} className="text-amber-400"/> Classifica netta per team
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[9px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
                  <th className="text-left px-2 py-1.5">Team</th>
                  <th className="text-center px-2 py-1.5">✅</th>
                  <th className="text-center px-2 py-1.5">❌</th>
                  <th className="text-right px-2 py-1.5">Premi</th>
                  <th className="text-right px-2 py-1.5">Penalità</th>
                  <th className="text-right px-2 py-1.5">Bet vinti</th>
                  <th className="text-right px-2 py-1.5">Bet persi</th>
                  <th className="text-right px-2 py-1.5">Netto</th>
                </tr>
              </thead>
              <tbody>
                {data.aggregates.map((a, idx) => (
                  <tr key={a.team_id}
                    data-testid={`agg-row-${a.team_id}`}
                    className={`border-t border-slate-800 hover:bg-slate-800/40 ${
                      idx === 0 ? "bg-amber-500/15 border-l-4 border-l-amber-400" :
                      idx <= 2 ? "bg-emerald-500/10 border-l-2 border-l-emerald-500/60" : ""
                    }`}>
                    <td className="px-2 py-1.5 font-bold text-white">
                      {idx === 0 && <span className="mr-1">👑</span>}{a.team_name}
                    </td>
                    <td className="px-2 py-1.5 text-center text-emerald-300 font-black">{a.count_achieved || 0}</td>
                    <td className="px-2 py-1.5 text-center text-rose-300 font-black">{a.count_failed || 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{a.total_prize > 0 ? `+${a.total_prize}` : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-rose-300">{a.total_penalty > 0 ? `−${a.total_penalty}` : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-amber-300">{a.total_bet_won > 0 ? `+${a.total_bet_won}` : "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-slate-500">{a.total_bet_lost > 0 ? `−${a.total_bet_lost}` : "—"}</td>
                    <td className={`px-2 py-1.5 text-right font-mono font-black ${a.net_delta > 0 ? "text-emerald-300" : a.net_delta < 0 ? "text-rose-300" : "text-slate-400"}`}>
                      {a.net_delta > 0 ? "+" : ""}{a.net_delta}c
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lista dettagliata */}
      {!loading && (
        <div className="space-y-2" data-testid="history-list">
          {(!data?.items?.length) && (
            <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 p-8 text-center text-slate-400 text-sm">
              Nessuna sponsorship trovata con i filtri correnti.
            </div>
          )}
          {data?.items?.map(item => (
            <HistoryRow key={item.sponsorship_id} item={item}
              expanded={!!expanded[item.sponsorship_id]}
              onToggle={() => setExpanded(x => ({ ...x, [item.sponsorship_id]: !x[item.sponsorship_id] }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ item, expanded, onToggle }) {
  const statusIcon = {
    achieved: <CheckCircle2 size={14} className="text-emerald-400"/>,
    failed: <XCircle size={14} className="text-rose-400"/>,
    cancelled: <PauseCircle size={14} className="text-slate-400"/>,
    active: <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"/>,
  }[item.status] || null;
  const statusColor = {
    achieved: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    failed: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    cancelled: "text-slate-400 border-slate-700 bg-slate-900",
    active: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  }[item.status] || "text-slate-300 border-slate-700";
  const statusLabel = {
    achieved: "Obiettivo raggiunto", failed: "Obiettivo mancato",
    cancelled: "Cancellato", active: "In corso",
  }[item.status] || item.status;

  const net = (item.prize_paid || 0) - (item.penalty_applied || 0) + (item.bet_payout || 0) - ((item.bet_status === "lost" ? item.bet_amount : 0) || 0);

  return (
    <div className={`rounded-2xl border ${statusColor} p-3 sm:p-4`} data-testid={`history-item-${item.sponsorship_id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {item.sponsor_logo_url && (
            <img src={item.sponsor_logo_url} alt="" className="w-10 h-10 rounded bg-white p-0.5 shrink-0"/>
          )}
          <div className="min-w-0">
            <div className="font-black text-white truncate">{item.sponsor_name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-2 flex-wrap">
              <span>{item.team_name}</span>
              <span className={item.category === "bonus_initial" ? "text-emerald-300" : "text-amber-300"}>
                · {item.category === "bonus_initial" ? "Bonus" : "Pagato"}
              </span>
              {item.chosen_at && <span className="text-slate-600">· {new Date(item.chosen_at).toLocaleDateString("it-IT")}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest">
          {statusIcon} <span>{statusLabel}</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {item.settle_final_position != null && (
            <span className="px-2 py-1 rounded-md bg-slate-900 border border-slate-700 text-[10px] font-mono">
              Pos {item.settle_final_position}° · {item.settle_percent}%
            </span>
          )}
          {item.bet_amount > 0 && (
            <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest flex items-center gap-1 ${
              item.bet_status === "won" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" :
              item.bet_status === "lost" ? "bg-rose-500/20 text-rose-300 border border-rose-500/40" :
              "bg-slate-800 text-slate-400 border border-slate-700"}`}>
              <Zap size={10}/>
              {item.bet_status === "won" ? `+${item.bet_payout}c 2x` :
               item.bet_status === "lost" ? `−${item.bet_amount}c persi` :
               `${item.bet_amount}c bet`}
            </span>
          )}
          <span className={`px-2 py-1 rounded-md font-mono font-black text-xs ${
            net > 0 ? "text-emerald-300 bg-emerald-500/10 border border-emerald-500/40" :
            net < 0 ? "text-rose-300 bg-rose-500/10 border border-rose-500/40" :
            "text-slate-400 bg-slate-900 border border-slate-700"
          }`}>
            {net > 0 ? "+" : ""}{net}c
          </span>
          <button onClick={onToggle} className="text-slate-400 hover:text-white" data-testid={`toggle-detail-${item.sponsorship_id}`}>
            {expanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-800/60 grid sm:grid-cols-3 gap-2 text-xs" data-testid={`detail-${item.sponsorship_id}`}>
          {item.objective_desc && (
            <div className="sm:col-span-3 flex items-start gap-1.5 text-slate-300">
              <Trophy size={12} className="mt-0.5 text-amber-400"/>
              <span><span className="font-bold text-white">Obiettivo:</span> {item.objective_desc}</span>
            </div>
          )}
          <Detail k="Premio pagato" v={item.prize_paid ? `+${item.prize_paid}c` : "—"} tone={item.prize_paid ? "emerald" : "slate"}/>
          <Detail k="Penalità" v={item.penalty_applied ? `−${item.penalty_applied}c` : "—"} tone={item.penalty_applied ? "rose" : "slate"}/>
          <Detail k="Upfront iniziale" v={`${item.category === "bonus_initial" ? "+" : "−"}${item.upfront_credits}c`} tone="slate"/>
          {item.bet_amount > 0 && (
            <>
              <Detail k="Scommessa" v={`${item.bet_amount}c`} tone="amber"/>
              <Detail k="Bet payout" v={item.bet_payout ? `+${item.bet_payout}c` : "0c"} tone={item.bet_payout > 0 ? "amber" : "slate"}/>
              <Detail k="Esito bet" v={item.bet_status === "won" ? "Raddoppiato" : item.bet_status === "lost" ? "Perso" : "—"} tone={item.bet_status === "won" ? "emerald" : "rose"}/>
            </>
          )}
          {item.settled_at && (
            <div className="sm:col-span-3 text-[10px] font-mono text-slate-500">
              Chiuso il {new Date(item.settled_at).toLocaleString("it-IT")}
              {item.settled_via === "bulk_finalize" && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">via finalize stagione</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Detail({ k, v, tone = "slate" }) {
  const map = {
    slate: "text-slate-300", emerald: "text-emerald-300",
    rose: "text-rose-300", amber: "text-amber-300",
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-black">{k}</div>
      <div className={`font-mono font-black ${map[tone]}`}>{v}</div>
    </div>
  );
}
